import { useCallback, useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import {
  useFirmNotificationPoliciesQuery,
  usePutFirmNotificationPoliciesMutation,
  type FirmNotificationPolicy,
} from '@/lib/api/firm-notifications';
import { NotificationPolicyEditor } from '@/components/settings/notification-policy-editor';

/**
 * Firm notifications — `/settings/notifications` (T-0112 / C20-122,
 * E09-F04 firm scope).
 *
 * Renders the firm-wide notification policy editor: one row per
 * event_type with channel toggles (in-app / email / digest), a severity
 * threshold selector, and audience scope chips. Per `handoff-inventory-
 * protocol.md` §1 the prototype at
 * `design_handoff_caliber_v2/prototype/screens-firm.js → firmNotifs()`
 * is the visual contract; this route stitches the data source to the
 * editor component.
 *
 * Backend: PR #133 (T-0112 follow-up) shipped the firm-scoped policy
 * CRUD endpoints; this route reads via `useFirmNotificationPoliciesQuery`
 * and writes via the bulk PUT mutation. See `firm-notifications.ts`
 * head-of-file for the UI ↔ wire shape mapping.
 */
export const Route = createFileRoute('/settings/notifications')({
  component: NotificationsSettings,
});

function NotificationsSettings() {
  const policiesQuery = useFirmNotificationPoliciesQuery();
  const putMutation = usePutFirmNotificationPoliciesMutation();

  const handleSave = useCallback(
    (next: FirmNotificationPolicy[]) => {
      putMutation.mutate(next);
    },
    [putMutation],
  );

  const categoryCount = useMemo(() => {
    if (!policiesQuery.data) return 0;
    return new Set(policiesQuery.data.policies.map((p) => p.category)).size;
  }, [policiesQuery.data]);

  if (policiesQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-body-sm text-ink-500">
        <Spinner /> Loading notification policies…
      </div>
    );
  }
  if (policiesQuery.error) {
    return <ErrorMessage error={policiesQuery.error} />;
  }
  if (!policiesQuery.data) {
    return <p className="text-body-sm text-ink-500">No notification policies available.</p>;
  }

  const data = policiesQuery.data;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="t-h1 font-semibold text-ink-900">Notification policies</h1>
          <p className="t-meta text-ink-500">
            Firm-wide defaults · {data.policies.length} event types across {categoryCount} categor
            {categoryCount === 1 ? 'y' : 'ies'}
          </p>
        </div>
      </header>

      {putMutation.error ? <ErrorMessage error={putMutation.error as Error} /> : null}

      <NotificationPolicyEditor
        policies={data.policies}
        onSave={handleSave}
        saving={putMutation.isPending}
      />
    </div>
  );
}
